// src/workflow/MyWorkflowDetail.jsx
import React, { useEffect, useState } from "react";
import {
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Badge,
  Box,
  Image,
  Flex,
  Button,
  HStack,
  Heading,
  Input,
  Spinner,
  Stack,
  Table,
  Tbody,
  Td,
  Th,
  FormLabel,
  Thead,
  Tr,
  Tag,
  TagLabel,
  Text,
  IconButton,
  Tooltip,
} from "@chakra-ui/react";
import {
  HiOutlineArrowNarrowLeft,
  HiOutlineSave,
  HiOutlinePlus,
  HiOutlineTrash,
} from "react-icons/hi";

import HeroImage from "./images/stamps.png";
import { useNavigate, useParams } from "react-router-dom";
import Navbar from "./Navbar";
import PartySelectById from "./PartySelectById";
import { errorFromResponse } from "./utils/apiErrors";

const fmtDate = (d) => (d ? new Date(d).toLocaleString() : "—");
const statusColor = (s) =>
  ({
    new: "gray",
    creating: "purple",
    running: "green",
    in_progress: "blue",
    paused: "orange",
    completed: "green",
    failed: "red",
  }[String(s || "").toLowerCase()] || "gray");

async function apiGet(url) {
  const r = await fetch(url);
  if (!r.ok) throw await errorFromResponse(r, `${r.status} ${r.statusText}`);
  return r.json();
}
async function apiPost(url, body) {
  const r = await fetch(url, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body ?? {}),
  });
  if (!r.ok) throw await errorFromResponse(r, `${r.status} ${r.statusText}`);
  try {
    return await r.json();
  } catch {
    return {};
  }
}

export default function MyWorkflowDetail({
  baseUrl = "https://workflow.poc.mrc.ai",
  productCode,
}) {
  const [localProduct, setLocalProduct] = useState(productCode || {});
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [ap, setAp] = useState(null);
  const [partyId, setPartyId] = useState("");
  const [nowIso, setNowIso] = useState(() => new Date().toISOString());

  const load = async () => {
    setLoading(true);
    try {
      const data = await apiGet(`${baseUrl}/api/temporal/activities/${id}`);
      setAp(data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load(); /* eslint-disable-next-line */
  }, [id]);

  const completeActivity = async (activityId) => {
    if (!partyId) return;
    await apiPost(
      `${baseUrl}/api/temporal/activities/${id}/activities/${activityId}/complete`,
      {
        byPartyId: partyId,
        atUtc: nowIso,
        activityId,
      }
    );
    await load();
  };

  const toLocalInput = (iso) => {
    try {
      const d = new Date(iso);
      const pad = (n) => String(n).padStart(2, "0");
      return `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(
        d.getUTCDate()
      )}T${pad(d.getUTCHours())}:${pad(d.getUTCMinutes())}`;
    } catch {
      return "";
    }
  };
  const fromLocalInputToIso = (local) =>
    local ? new Date(local + "Z").toISOString() : new Date().toISOString();

  const handleClose = () => {
    // Change this to window.history.back()
    window.history.back();
  };

  return (
    <Box>
      <Navbar />
      <Box position="relative" width="100%" minHeight="100vh">
        <Image
          src={HeroImage}
          alt="Hero Watermark"
          width="100%"
          height="100%"
          objectFit="cover"
          opacity={0.35}
          position="absolute"
          top={0}
          left={0}
          zIndex={-1}
          filter="blur(1px)"
        />

        <Box position="relative" zIndex={1} p={10}>
          <Box
            borderWidth="1px"
            borderRadius="md"
            p={1}
            mb={2}
            borderColor="gray.500"
            backgroundColor="whiteAlpha.800">
            <Flex mb={2}>
              <HStack spacing={6}>
                <Tooltip label="Back to the Environment Homepage">
                  <IconButton
                    icon={<HiOutlineArrowNarrowLeft />}
                    size="md"
                    onClick={handleClose}
                  />
                </Tooltip>
                <Heading size="lg">Workflow Detail</Heading>

                <Tooltip label="Workflow">
                  <IconButton icon={<HiOutlineSave />} />
                </Tooltip>
              </HStack>
            </Flex>
          </Box>


          <Box
            borderWidth="1px"
            borderRadius="md"
            p={1}
            mb={2}
            borderColor="gray.500"
            backgroundColor="whiteAlpha.800">
            <Stack spacing={4} p={4}>
              {ap && !loading && (
                <>
                  <Stack spacing={0}>
                    <Heading size="lg">{ap.name || "—"}</Heading>
                    <HStack color="gray.600" fontSize="sm" wrap="wrap">
                      <Tag size="sm" colorScheme="purple">
                        <TagLabel>{ap.type}</TagLabel>
                      </Tag>
                      <Text>• ID: {ap._id}</Text>
                      {ap.workflowId && (
                        <>
                          <Text>•</Text>
                          <Text>WF: {ap.workflowId}</Text>
                        </>
                      )}
                      {ap.status && (
                        <>
                          <Text>•</Text>
                          <Badge colorScheme={statusColor(ap.status)}>
                            {ap.status}
                          </Badge>
                        </>
                      )}
                    </HStack>
                  </Stack>

                            <Box>
            {ap && (
              <HStack spacing={3} mt={2} flexWrap="wrap">
                {(!ap.workflowId || ap.status === "creating") && (
                  <Button
                    size="sm"
                    onClick={async () => {
                      await apiPost(
                        `${baseUrl}/api/temporal/activities/${id}/start`
                      );
                      await load();
                    }}>
                    Start / Repair Workflow
                  </Button>
                )}
                {ap.workflowId && (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={async () => {
                      await apiPost(
                        `${baseUrl}/api/temporal/activities/${id}/signals/refresh`
                      );
                      await load();
                    }}>
                    Refresh Workflow State
                  </Button>
                )}
              </HStack>
            )}
          </Box>

                  <Box borderWidth="1px" borderRadius="lg" p={3}>
                    <HStack gap={3} wrap="wrap">

                      <PartySelectById
                        label="Workflow Party"
                        selectedPartyId={partyId}
                        onChange={(value) => setPartyId(value)}
                      />

                      <Text fontWeight="semibold">Completed at (UTC)</Text>
                      <Input
                        size="sm"
                        type="datetime-local"
                        value={toLocalInput(nowIso)}
                        onChange={(e) =>
                          setNowIso(fromLocalInputToIso(e.target.value))
                        }
                        width="230px"
                      />
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setNowIso(new Date().toISOString())}>
                        Now
                      </Button>
                    </HStack>
                  </Box>

                  {/* --- Stages as Tabs --- */}
                  {!ap?.stages || ap.stages.length === 0 ? (
                    <Text color="gray.500">No stages.</Text>
                  ) : (
                    (() => {
                      const stages = (ap.stages || [])
                        .slice()
                        .sort((a, b) => (a.number ?? 0) - (b.number ?? 0));

                      // Default tab: first non-completed stage (else 0)
                      const firstActiveIdx = stages.findIndex(
                        (s) =>
                          String(s.status || "").toLowerCase() !== "completed"
                      );
                      const defaultIndex =
                        firstActiveIdx >= 0 ? firstActiveIdx : 0;

                      return (
                        <Tabs
                          variant="enclosed"
                          defaultIndex={defaultIndex}
                          isLazy>
                          <TabList overflowX="auto" whiteSpace="nowrap">
                            {stages.map((s) => (
                              <Tab key={s.id || s.number}>
                                <HStack spacing={2}>
                                  <Text>Stage {s.number ?? "—"}</Text>
                                  <Badge colorScheme={statusColor(s.status)}>
                                    {s.status || "—"}
                                  </Badge>
                                  {s?.time?.notBeforeUtc && (
                                    <Tag size="sm">
                                      <TagLabel>
                                        Gate: {fmtDate(s.time.notBeforeUtc)}
                                      </TagLabel>
                                    </Tag>
                                  )}
                                  {s?.time?.deadlineUtc && (
                                    <Tag size="sm">
                                      <TagLabel>
                                        Deadline: {fmtDate(s.time.deadlineUtc)}
                                      </TagLabel>
                                    </Tag>
                                  )}
                                </HStack>
                              </Tab>
                            ))}
                          </TabList>

                          <TabPanels>
                            {stages.map((s) => (
                              <TabPanel key={s.id || s.number} px={0}>
                                <Box overflowX="auto">
                                  <Table size="sm">
                                    <Thead>
                                      <Tr>
                                        <Th>Activity</Th>
                                        <Th>Type</Th>
                                        <Th>Status</Th>
                                        <Th>SLA Due</Th>
                                        <Th>Actions</Th>
                                      </Tr>
                                    </Thead>
                                    <Tbody>
                                      {(s.activities || []).map((a) => (
                                        <Tr key={a.id}>
                                          <Td>
                                            <Stack spacing={0}>
                                              <Text>{a.name || "—"}</Text>
                                              <Text
                                                fontSize="xs"
                                                color="gray.500">
                                                {a.id}
                                              </Text>
                                            </Stack>
                                          </Td>
                                          <Td>{a.type || "—"}</Td>
                                          <Td>
                                            <Badge
                                              colorScheme={statusColor(
                                                a.status
                                              )}>
                                              {a.status || "—"}
                                            </Badge>
                                          </Td>
                                          <Td>{fmtDate(a?.time?.slaDueUtc)}</Td>
                                          <Td>
                                            <Button
                                              size="xs"
                                              isDisabled={
                                                String(
                                                  a.status
                                                ).toLowerCase() ===
                                                  "completed" || !partyId
                                              }
                                              onClick={() =>
                                                completeActivity(a.id)
                                              }>
                                              Complete
                                            </Button>
                                          </Td>
                                        </Tr>
                                      ))}
                                      {(s.activities || []).length === 0 && (
                                        <Tr>
                                          <Td colSpan={5} color="gray.500">
                                            No activities.
                                          </Td>
                                        </Tr>
                                      )}
                                    </Tbody>
                                  </Table>
                                </Box>
                              </TabPanel>
                            ))}
                          </TabPanels>
                        </Tabs>
                      );
                    })()
                  )}
                </>
              )}
            </Stack>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
